<template>
    <div>
        <b-breadcrumb>
            <b-breadcrumb-item :to="{ name: 'product-overview' }">
                {{ $t('Page(ProductOverview):Component(Text):page_title') }}
            </b-breadcrumb-item>
            <b-breadcrumb-item :to="productRoute">{{ productTitle || '#' + id }}</b-breadcrumb-item>
            <b-breadcrumb-item active>{{ $t('reports') }}</b-breadcrumb-item>
        </b-breadcrumb>
        <b-card>
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
                <h2 class="mb-1">{{ $t('reports') }}: {{ productTitle || '#' + id }}</h2>
                <b-button :to="productRoute" variant="outline-secondary" class="mb-1">
                    {{ $t('backToProduct') }}
                </b-button>
            </div>
            <b-alert :show="loadFailed" variant="danger"><div class="alert-body">{{ $t('loadFailed') }}</div></b-alert>
            <b-form @submit.prevent="createReport">
                <b-form-group :label="$t('documentType')" label-for="report-document-type">
                    <b-form-radio-group
                        id="report-document-type"
                        v-model="documentType"
                        :options="documentTypes"
                        name="report-document-type"
                        :disabled="loading || loadFailed"
                        stacked
                        @change="reportRequested = false"
                    />
                </b-form-group>
                <b-form-group :label="$t('regulations')" label-for="report-regulations">
                    <v-select
                        v-model="selectedRegulations"
                        input-id="report-regulations"
                        multiple
                        label="title"
                        :options="regulations"
                        :placeholder="$t('selectRegulations')"
                        :loading="loading"
                        :disabled="loading || loadFailed"
                        @input="reportRequested = false"
                    />
                </b-form-group>
                <b-alert :show="canCreateReport" variant="warning" class="app-alert--attention">
                    <div class="alert-body d-flex align-items-center">
                        <feather-icon icon="InfoIcon" size="18" class="mr-75 flex-shrink-0" aria-hidden="true"/>
                        <span>{{ $t('noExistingDocument') }}</span>
                    </div>
                </b-alert>
                <b-button type="submit" variant="primary" :disabled="!canCreateReport">
                    {{ $t('createReport') }}
                </b-button>
                <b-alert :show="reportRequested" variant="warning" class="app-alert--attention mt-2 mb-0" aria-live="polite">
                    <div class="alert-body d-flex align-items-center">
                        <feather-icon icon="InfoIcon" size="18" class="mr-75 flex-shrink-0" aria-hidden="true"/>
                        <span>{{ $t('notImplemented') }}</span>
                    </div>
                </b-alert>
            </b-form>
        </b-card>
    </div>
</template>

<script>
import {
    BAlert, BBreadcrumb, BBreadcrumbItem, BButton, BCard, BForm, BFormGroup, BFormRadioGroup,
} from 'bootstrap-vue'
import VSelect from 'vue-select'
import { LegislationApi } from '@core/api'
import { ProductProvider } from '@core/domain/product'
import messages from './translations'

export default {
    components: { BAlert, BBreadcrumb, BBreadcrumbItem, BButton, BCard, BForm, BFormGroup, BFormRadioGroup, VSelect },
    i18n: { messages },
    props: {
        id: { type: [String, Number], required: true },
    },
    data() {
        return {
            productTitle: '',
            regulations: [],
            documentType: null,
            selectedRegulations: [],
            reportRequested: false,
            loading: true,
            loadFailed: false,
        }
    },
    computed: {
        productRoute() {
            return { name: 'product-card', params: { id: this.id } }
        },
        documentTypes() {
            return ['declaration', 'statement', 'technical'].map(value => ({ value, text: this.$t(value) }))
        },
        canCreateReport() {
            return !this.loading && !this.loadFailed && this.documentType !== null && this.selectedRegulations.length > 0
        },
    },
    watch: {
        id: { immediate: true, handler: 'loadParameters' },
    },
    methods: {
        async loadParameters() {
            const productId = this.id
            this.loading = true
            this.loadFailed = false
            this.productTitle = ''
            this.regulations = []
            this.documentType = null
            this.selectedRegulations = []
            this.reportRequested = false
            try {
                const [card, response] = await Promise.all([
                    ProductProvider(productId).getCard(),
                    LegislationApi.all(),
                ])
                if (this.id !== productId) return
                this.productTitle = card.names && card.names.length ? card.names[0].value : ''
                this.regulations = response.data
            } catch (error) {
                if (this.id === productId) this.loadFailed = true
            } finally {
                if (this.id === productId) this.loading = false
            }
        },
        createReport() {
            if (this.canCreateReport) this.reportRequested = true
        },
    },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';
</style>
